import videojs from "video.js";
import "./switch-button.js";
import "./modal-menu.js";
import "./videojs-group-with-quality.scss";
import { checkTouchScreen } from "../../js/helpers/checkTouchScreen";

const Plugin = videojs.getPlugin('plugin');

class GroupWithQuality extends Plugin {
	constructor(player, options) {
		super(player, options);

		player.addClass('vjs-group-with-quality');
		this.switchButton = null;
		this.controlBar = null;
		this.options = options;

		player.ready(() => {
			// setTimeout - ожидает появления всех кнопок
			setTimeout(() => {
				this.controlBar = this.player.getChild('ControlBar');
				this.hideButtons(options.componentNames);
				this.addSwitchButtonAndMenu(options);
				this.addRateToSettingsGroup(options);
				this.addQualityToSettingsGroup();
				this.setListeners();
			});
		});
	}

	setListeners() {
		const eventName = checkTouchScreen() ? 'touchstart' : 'click';

		// Закрытие модального окна кликом вне его содержимого
		const handleClick = (event) => {
			if (event.target.closest('.vjs-group-with-quality__switch')) return;
			if (this.modal && !this.modal.hasClass('vjs-hidden')) {
				this.hideModal();
			}
		};
		document.addEventListener(eventName, handleClick);
		this.player.on('dispose', () => {
			document.removeEventListener(eventName, handleClick);
		});

		if (this.switchButton) {
			// Скрытие/показ модального окна
			this.switchButton.el().addEventListener(eventName, (event) => {
				const isClickInModal = event.target.closest('.vjs-group-with-quality__modal-menu');
				if (isClickInModal) return;

				this.modal = this.switchButton.getChild('GroupWithQualityModalMenu');
				if (this.modal.hasClass('vjs-hidden')) {
					const restrictionModalDialog = this.player.getChild('restrictionModalDialog');
					if (restrictionModalDialog) {
						restrictionModalDialog.hide();
					}
					this.showModal();
				} else {
					this.hideModal();
				}
			});
		}
	}

	addSwitchButtonAndMenu(options) {
		if (!this.controlBar) return;
		this.switchButton = this.controlBar.addChild('GroupWithQualitySwitchButton');
		this.switchButton.addChild('GroupWithQualityModalMenu', videojs.mergeOptions(
			options,
			{
				isQualityUnavailable: this.isQualityUnavailable.bind(this),
				isQualityRestricted: this.isQualityRestricted.bind(this),
				showRestrictionModalDialog: this.showRestrictionModalDialog.bind(this),
				buttonToggle: this.switchButton,
				hideModal: () => this.hideModal()
			}
		));
	}

	addRateToSettingsGroup(options) {
		if (options.isLive) {
			return;
		}
		const {
			update: updateRatesInSettingsGroup
		} = this.player.settingsGroup().addSettingsSection({
			name: {
				text: 'Rate',
			},
			componentName: 'PlaybackRateMenuButton',
			id: 'rate',
			getCurrentValue: () => `${this.player.playbackRate()}X`,
			items: [1, 1.25, 1.5, 1.75, 2].map((rate) => ({
				name: `${rate}X`,
				checkSelected: () => rate === this.player.playbackRate(),
				checkDisabled: () => false,
				onClick: () => {
					this.player.playbackRate(rate);
					updateRatesInSettingsGroup();
				}
			}))
		});
		this.player.on(['loadstart', 'ratechange'], () => updateRatesInSettingsGroup());
	}

	addQualityToSettingsGroup() {
		if (!this.switchButton) return;
		let disposeQualitiesInSettingsGroup;
		let updateQualitiesInSettingsGroup;
		const modal = this.switchButton.getChild('GroupWithQualityModalMenu');
		const addQualities = (isHidden) => {
			if (modal.qualityList.length < 2) {
				return;
			}
			const { update, dispose } = this.player.settingsGroup().addSettingsSection({
				name: {
					text: 'Quality'
				},
				componentName: 'qualitySwitcherButton',
				id: 'quality',
				isHidden,
				getCurrentValue: () => {
					const selectedQuality = modal.qualityList.find((quality) => quality.checkSelected());
					if (!selectedQuality) return '';
					return selectedQuality.controlText();
				},
				items: modal.qualityList.map((quality) => {
					const qualityName = quality.getAttribute('data-quality-name');
					return {
						name: quality.controlText(),
						checkSelected: () => quality.checkSelected(),
						checkDisabled: () => false,
						onClick: () => {
							if (this.isQualityRestricted(qualityName)) {
								this.showRestrictionModalDialog(() => {
									this.options.requestAccess(qualityName);
								});
								return;
							}
							quality.handleClick();
						},
						className: this.isQualityRestricted(qualityName) && ((defaultClassName) => `${defaultClassName}_restricted`)
					};
				})
			});
			updateQualitiesInSettingsGroup = update;
			disposeQualitiesInSettingsGroup = dispose;
		};
		modal.on('qualities-updated', () => {
			let wasHidden = true;
			if (disposeQualitiesInSettingsGroup) {
				wasHidden = disposeQualitiesInSettingsGroup().wasHidden;
			}
			addQualities(wasHidden);
		});
		modal.on('quality-selected', () => {
			if (updateQualitiesInSettingsGroup) {
				updateQualitiesInSettingsGroup();
			}
		});

		addQualities();
	}

	showModal() {
		this.switchButton.addClass('vjs-group-with-quality__switch_open');
		this.switchButton.getChild('GroupWithQualityModalMenu').show();
	}

	hideModal() {
		//при выборе качества (клик на пункте меню) не всегда отрабатывает событие mouseleave,
		//что, в свою очередь, нарушает определение активности пользователя, поэтому остаются висеть
		//открытыми контролы. Поэтому дополнительно назаначен триггер mouseleave
		this.switchButton.removeClass('vjs-group-with-quality__switch_open');
		this.switchButton.getChild('GroupWithQualityModalMenu').hide();
		this.controlBar.trigger('mouseleave');
	}

	/**
	 * Проверка качества на доступность отображения.
	 *
	 * @param {String} quality - Название качества.
	 * @returns {Boolean}
	 */
	isQualityUnavailable(quality) {
		if (!this.options.unavailableQualities || this.options.unavailableQualities.length === 0) {
			return false;
		}

		return this.options.unavailableQualities.includes(quality);
	}

	/**
	 * Проверка наличия платных ограничений для качества.
	 *
	 * @param {String} quality - Название качества.
	 * @returns {Boolean}
	 */
	isQualityRestricted(quality) {
		if (!this.options.restrictedQualities || this.options.restrictedQualities.length === 0) {
			return false;
		}

		return this.options.restrictedQualities.includes(quality);
	}

	showRestrictionModalDialog(getAccessCallback) {
		const restrictionModalDialog = this.player.getChild('restrictionModalDialog');
		if (!restrictionModalDialog) return;
		restrictionModalDialog.toggleDisplay();
		restrictionModalDialog.onGetAccess(() => getAccessCallback());
	}

	hideButtons(componentNames) {
		if (!this.controlBar) return;
		componentNames.forEach((name) => {
			const component = this.controlBar.getChild(name);
			if (!component) return;
			component.addClass('vjs-group-with-quality__item_hide');
		});
	}
}

videojs.registerPlugin('groupWithQuality', GroupWithQuality);
